import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateCreative({ user, projects, language = 'id' }) {
  const fallbackAvatar = 'https://ui-avatars.com/api/?name=User&background=ec4899&color=fff';

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white p-4 sm:p-6 md:p-8 max-w-[21cm] mx-auto shadow-sm print:p-8 print:shadow-none print:max-w-full" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Creative Header with Photo */}
      <div className="cv-section relative bg-gradient-to-r from-pink-500 to-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 md:mb-8 text-white avoid-break print:bg-gray-800 print:rounded-none print:text-white">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
          <div className="cv-photo w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-white shadow-xl overflow-hidden flex-shrink-0 bg-white print:w-32 print:h-32 print:shadow-none print:border-gray-400">
            {user?.photo ? (
              <img src={user.photo} alt={user.full_name} className="w-full h-full object-cover" />
            ) : (
              <img src={fallbackAvatar} alt="Avatar" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">{getFormattedUserName(user) || 'Your Name'}</h1>
            <p className="text-base sm:text-lg md:text-xl font-light mb-2 sm:mb-3">{user?.job || 'Professional Title'}</p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-4 text-xs sm:text-sm">
              {user?.email && <span>📧 {user.email}</span>}
              {user?.phone && <span>📱 {user.phone}</span>}
              {user?.location && <span>📍 {user.location}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Sidebar */}
        <div className="col-span-1 space-y-4 sm:space-y-6">
          {/* Skills with Progress Bars */}
          {user?.skills && user.skills.length > 0 && (
            <div className="cv-section bg-gradient-to-br from-pink-50 to-blue-50 rounded-lg sm:rounded-xl p-4 sm:p-5 avoid-break print:bg-gray-100 print:rounded-none">
              <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="text-pink-600">💡</span>
                {t('skills', language)}
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {user.skills.map((skill, i) => (
                  <div key={i}>
                    <p className="text-xs sm:text-sm font-medium text-gray-800 mb-1">{skill}</p>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden print:rounded-sm">
                      <div 
                        className="h-full bg-gradient-to-r from-pink-500 to-blue-600 print:bg-gray-600"
                        style={{ width: `${85 + (i % 3) * 5}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects Highlight */}
          {projects && projects.length > 0 && (
            <div className="cv-section bg-gradient-to-br from-blue-50 to-pink-50 rounded-lg sm:rounded-xl p-4 sm:p-5 avoid-break print:bg-gray-100 print:rounded-none">
              <h2 className="cv-section-title text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="text-blue-600">🚀</span>
                {t('projects', language)}
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {projects.slice(0, 10).map((proj, i) => (
                  <div key={i} className="cv-project-item pb-2 sm:pb-3 border-b border-blue-200 last:border-0 avoid-break">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{proj.title}</h3>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{proj.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="col-span-1 sm:col-span-2 space-y-4 sm:space-y-6">
          {/* About Me */}
          {user?.summary && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-pink-500 to-blue-600 rounded"></div>
                {t('summary', language)}
              </h2>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed text-justify">{user.summary}</p>
            </div>
          )}

          {/* Experience */}
          {user?.experience && user.experience.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-pink-500 to-blue-600 rounded print:bg-gray-600 print:rounded-none"></div>
                {t('workExperience', language)}
              </h2>
              <div className="space-y-4 sm:space-y-5">
                {user.experience.map((exp, i) => (
                  <div key={i} className="cv-experience-item relative pl-4 sm:pl-6 border-l-2 border-pink-200 avoid-break print:border-gray-400">
                    <div className="absolute -left-1.5 sm:-left-2 top-0 w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gradient-to-br from-pink-500 to-blue-600 print:bg-gray-600"></div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">{exp.position}</h3>
                    <p className="text-xs sm:text-sm text-pink-600 font-medium print:text-gray-700">{exp.company}</p>
                    <p className="text-xs text-gray-500 mb-1 sm:mb-2">{exp.period}</p>
                    {exp.description && (
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed text-justify">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education - Higher Education */}
          {higherEducation.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-pink-500 to-blue-600 rounded print:bg-gray-600 print:rounded-none"></div>
                {t('higherEducation', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {higherEducation.map((edu, i) => (
                  <div key={i} className="cv-education-item bg-gray-50 rounded-lg p-3 sm:p-4 avoid-break">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs sm:text-sm text-blue-600 font-medium">{edu.school || edu.institution}</p>
                    <p className="text-xs text-gray-500">{edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education - K-12 */}
          {k12Education.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-pink-500 to-blue-600 rounded print:bg-gray-600 print:rounded-none"></div>
                {t('formalEducation', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {k12Education.map((edu, i) => (
                  <div key={i} className="cv-education-item bg-gray-50 rounded-lg p-3 sm:p-4 avoid-break print:bg-white print:border print:border-gray-300 print:rounded-none">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs sm:text-sm text-blue-600 font-medium print:text-gray-700">{edu.school || edu.institution}</p>
                    <p className="text-xs text-gray-500">{edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
